import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import UsersTable from "../../components/admin/UsersTable"
import UserFormModal from "../../components/admin/UserFormModal"
import UserViewModal from "../../components/admin/UserViewModal"
import LearnerDetailModal from "../../components/admin/learners/LearnerDetailModal"
import DashboardStats from "../../components/admin/stats/DashboardStats"
import { useAdminStats } from "../../hooks/useAdminStats"
import { useAdminDashboardStats } from "../../hooks/useStats"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("dashboard")
  const [searchTerm, setSearchTerm] = useState("")
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [viewingUser, setViewingUser] = useState(null)
  const [groupBy, setGroupBy] = useState("month")

  // Récupération des données legacy pour les utilisateurs récents
  const { recentUsers, loading: legacyLoading, error: legacyError, refetch: legacyRefetch } = useAdminStats()

  // Récupération des stats via la nouvelle API /stats/dashboard
  const dashboardParams = useMemo(() => ({ groupBy }), [groupBy])
  const {
    overview,
    users: usersStats,
    courses: coursesStats,
    enrollments: enrollmentsStats,
    revenue: revenueStats,
    engagement: engagementStats,
    webinars: webinarsStats,
    newsletter: newsletterStats,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useAdminDashboardStats(dashboardParams)

  const loading = statsLoading || legacyLoading
  const error = statsError || legacyError
  const refetch = () => {
    refetchStats()
    legacyRefetch()
  }

  const filteredUsers = (recentUsers || []).filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreateUser = () => {
    setEditingUser(null)
    setIsUserModalOpen(true)
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setIsUserModalOpen(true)
  }

  const handleDeleteUser = async (user) => {
    const { toastUtils } = await import("../../utils/toast")
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.name} ?`)) {
      try {
        await api.delete(endpoints.deleteUser(user.id))
        toastUtils.success("Utilisateur supprimé avec succès")
        refetch()
      } catch (error) {
        toastUtils.error(error.message || "Erreur lors de la suppression")
      }
    }
  }

  const handleUserSuccess = async () => {
    console.log('[AdminDashboard] handleUserSuccess appelé')
    console.log('[AdminDashboard] Utilisateur modifié:', editingUser)
    setEditingUser(null)
    setIsUserModalOpen(false)
    // Petit délai pour s'assurer que le backend a bien enregistré les modifications
    console.log('[AdminDashboard] Attente de 500ms avant refetch...')
    await new Promise(resolve => setTimeout(resolve, 500))
    console.log('[AdminDashboard] Appel de refetch()...')
    refetch()
    console.log('[AdminDashboard] refetch() appelé, données should refresh')
  }

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      <AdminSidebar />

      {/* Main Content */}
      <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden">
        <AdminHeader
          title="Dashboard Admin"
          subtitle="Vue d'ensemble de la plateforme"
        />

        {/* Affichage d'erreur si nécessaire */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-semibold">Erreur de chargement</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* All Stats Sections */}
        <DashboardStats
          overview={overview}
          users={usersStats}
          courses={coursesStats}
          enrollments={enrollmentsStats}
          revenue={revenueStats}
          engagement={engagementStats}
          webinars={webinarsStats}
          newsletter={newsletterStats}
          loading={loading}
          groupBy={groupBy}
          onChangeGroupBy={setGroupBy}
        />

        {/* Users Table */}
        <div className="mb-8">
          <div className="mb-4 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#203a8a]">Liste des utilisateurs</h3>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un utilisateur..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button 
                  onClick={handleCreateUser}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                >
                  Ajouter un utilisateur
                </button>
              </div>
            </div>
          </div>
          <UsersTable 
            users={filteredUsers} 
            onView={(user) => setViewingUser(user)}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
          />
        </div>

        {/* User Form Modal */}
        <UserFormModal
          isOpen={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          user={editingUser}
          onSuccess={handleUserSuccess}
        />

        {/* Learner Detail Modal */}
        <LearnerDetailModal
          isOpen={!!viewingUser}
          userId={viewingUser?.id}
          onClose={() => setViewingUser(null)}
        />
      </main>
    </div>
  )
}
